import { User } from "firebase/auth";
import {
  collection,
  doc,
  DocumentData,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  startAfter,
  where,
  WriteBatch,
  writeBatch,
} from "firebase/firestore";
import { AnalysisDocument, FavoritDocument } from "~/src/type/firebase/firestore";
import { HeadingFavoritArticle } from "~/src/type/ui/article/article";
import { firestore } from "../firebase/config";
import { ReadPartQueryResult } from "./analysis";
import { parseFavoritFirestoreToAnalysisDocumentList } from "./common";
import { COLLECTION_FAVORIT } from "./info";
import { updateAddUserFavoritList, updateRemoveUserFavoritList } from "./user";

// 無効ないいね数
export const INVARIT_FAVORIT_COUNT = -1;

/**
 * 指定ユーザーのいいね分析記事（ヘッダ情報のみのリスト）を取得
 *   from いいねコレクション（トップ）
 */
export async function readFavoritList(
  readSize: number,
  userid: string,
  justBeforeArticle?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  //------------
  // クエリ
  //------------
  // いいねコレクションから、ユーザーが付与したいいねを取得
  let q = query(
    collection(firestore, COLLECTION_FAVORIT),
    where("giveUserId", "==", userid),
    orderBy("favoritCreatedAt", "desc"),
    limit(readSize)
  );

  // 直前の読み込み記事があれば、その次から取得
  if (justBeforeArticle) {
    q = query(q, startAfter(justBeforeArticle));
  }

  //------------
  // 取得
  //------------
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    // データなしの場合、空データを返す
    return {
      readLast: undefined,
      list: [],
    };
  }

  //------------
  // パース
  //------------
  const favoritArticleList: HeadingFavoritArticle[] =
    await parseFavoritFirestoreToAnalysisDocumentList(querySnapshot);

  // リストで返す
  return {
    readLast: querySnapshot.docs[querySnapshot.docs.length - 1],
    list: favoritArticleList,
  };
}

/**
 * いいね記録横断処理
 * ・「いいねコレクション」  ：いいね記録を新規作成
 * ・「ユーザーコレクション」：いいねリストに分析記事を追加
 * ・「分析記事コレクション」：functionsで対応
 */
export async function createFavorit(user: User, analysisId: string, contents: AnalysisDocument) {
  const { uid } = user;

  const batch = writeBatch(firestore);

  //------------
  // いいね記録を追加
  //------------
  const favorit: FavoritDocument = {
    analysisId: analysisId,
    giveUserId: uid,
    favoritCreatedAt: serverTimestamp(),

    // 非正規形の分析記事情報
    userId: contents.userId,
    userName: contents.userName,
    title: contents.title,
    createdAt: contents.createdAt,
    updatedAt: contents.updatedAt,
    works: contents.works,
  };

  // 追加
  const newDocRef = doc(collection(firestore, COLLECTION_FAVORIT));
  batch.set(newDocRef, favorit);

  //------------
  // いいね数を加算
  // @note functionsで実施
  //------------

  //------------
  // ユーザーのいいねリストに追加
  //------------
  await updateAddUserFavoritList(batch, uid, analysisId);

  //============
  // コミット
  //============
  await batch.commit();

  console.log("いいね記録作成完了 batch.commit()");
}

/**
 * いいね削除横断処理
 * ・「いいねコレクション」  ：いいね記録を削除
 * ・「ユーザーコレクション」：いいねリストから分析記事を削除
 * ・「分析記事コレクション」：functionsで対応
 */
export async function deleteFavorit(user: User, analysisId: string) {
  const { uid } = user;

  const batch = writeBatch(firestore);

  //------------
  // いいね記録を削除
  //------------
  const q = query(
    collection(firestore, COLLECTION_FAVORIT),
    where("analysisId", "==", analysisId),
    where("giveUserId", "==", uid)
  );

  const favoritDoc = await getDocs(q);
  if (!favoritDoc.empty) {
    // @note 1件しかない想定だが、複数削除で実装する
    for (const doc of favoritDoc.docs) {
      batch.delete(doc.ref);
    }
  }

  console.log("いいね記録削除 batch.delete() 削除");

  //------------
  // いいね数を減算
  // @note functionsで実施
  //------------

  //------------
  // ユーザーのいいねリストから削除
  //------------
  // batchへ渡すデータを取得
  await updateRemoveUserFavoritList(batch, uid, analysisId);

  console.log("いいね記録削除 batch.update() ユーザーのいいねリストから削除");

  //============
  // コミット
  //============
  await batch.commit();

  console.log("いいね記録削除 batch.commit()");
}

/**
 * いいねドキュメントのユーザー名更新
 */
export async function updateFavoritUserName(batch: WriteBatch, userId: string, userName: string) {
  //------------
  // クエリの生成
  //------------
  const q = query(collection(firestore, COLLECTION_FAVORIT), where("userId", "==", userId));

  //------------
  // 一括更新
  //------------
  const favoritDocs = await getDocs(q);
  favoritDocs.docs.forEach((item) => {
    batch.update(item.ref, { userName: userName });
  });
}
